import { signalVariants, type SignalVariantTypes } from '@etg/wings';
import { PassengerLegStatus } from '@eti/schema-types';
import { differenceInHours } from 'date-fns';
import type {
  FlightInformationType,
  TravelersType,
  TravelerType,
  TripSegmentType,
} from '../../../models';
import type { CheckInStatusInViewType, CheckInStatusType } from './models';
import { CheckInStatusInView } from './models';

/**
 * This method returns the check-in status information of a passenger.
 * -- In case the response from passNFly returns a 'REGISTRATION_NOT_FOUND' errorCode, the method returns:
 *     -- FAILED - for the segments that depart in less than the check-in window timeframe.
 *     -- PENDING - for the past segments or the segments that depart in more than the check-in window timeframe.
 * -- In case the response from passNFly returns a 'GENERIC_ERROR' errorCode, the method returns FAILED.
 * -- In case of no error, the method returns:
 *     -- the check-in status of the given passenger and segment, if the passenger and the segment are found in the response from passNFly
 *     -- FAILED, if the passenger and the segment are not found in the response from passNFly,
 */
const getPassNFlyPassengerStatus = (
  checkInStatus: NonNullable<CheckInStatusType>,
  checkInTimeframe: number,
  segment: TripSegmentType,
  traveler: TravelerType,
): PassengerLegStatus => {
  if (checkInStatus.errorCode === 'REGISTRATION_NOT_FOUND') {
    const today = new Date();
    const segmentDepartureTime = new Date(segment.departuredAt);
    const hoursLeftUntilDeparture = differenceInHours(segmentDepartureTime, today);
    return hoursLeftUntilDeparture <= 0 || hoursLeftUntilDeparture < checkInTimeframe
      ? PassengerLegStatus.Failed
      : PassengerLegStatus.Pending;
  }

  if (checkInStatus.errorCode === 'GENERIC_ERROR') {
    return PassengerLegStatus.Failed;
  }

  const passNFlyPassenger = checkInStatus.passengers?.find(
    (passenger) => traveler.name === `${passenger?.firstName} ${passenger?.lastName}`,
  );
  const passNFlyLeg = checkInStatus.legs?.find(
    (leg) =>
      leg?.departureAirport === segment.origin.code &&
      leg.arrivalAirport === segment.destination.code,
  );
  const passNFlyPassengerStatus = passNFlyLeg?.passengerLegs?.find(
    (passengerLeg) => passNFlyPassenger?.passengerId === passengerLeg?.passengerId,
  );

  return passNFlyPassengerStatus?.status || PassengerLegStatus.Failed;
};

export const getPassengersPerCheckInStatus = (
  checkInStatus: NonNullable<CheckInStatusType>,
  checkInTimeframe: number,
  segment: TripSegmentType,
  travelers: TravelersType,
) =>
  travelers.reduce(
    (acc: Record<PassengerLegStatus, TravelersType>, traveler) => {
      const status = getPassNFlyPassengerStatus(checkInStatus, checkInTimeframe, segment, traveler);
      if (!acc[status]) {
        acc[status] = [];
      }
      acc[status].push(traveler);
      return acc;
    },
    {} as Record<PassengerLegStatus, TravelersType>,
  );

export const getSegmentCheckInStatuses = (
  passengersPerCheckInStatus: Record<PassengerLegStatus, TravelersType>,
): PassengerLegStatus[] => {
  const statusesOrder: PassengerLegStatus[] = [
    PassengerLegStatus.Suspended,
    PassengerLegStatus.Failed,
    PassengerLegStatus.Missed,
    PassengerLegStatus.Pending,
    PassengerLegStatus.Success,
  ];

  const orderedCheckInStatuses: PassengerLegStatus[] = [];

  statusesOrder.forEach((status) => {
    if (passengersPerCheckInStatus[status]) {
      orderedCheckInStatuses.push(status);
    }
  });

  return orderedCheckInStatuses;
};

export const getSegmentCheckInStatus = (
  checkInStatuses: PassengerLegStatus[],
): CheckInStatusInViewType => {
  if (checkInStatuses.length > 1) {
    return CheckInStatusInView.ACTION_REQUIRED;
  }
  if (checkInStatuses[0] === PassengerLegStatus.Pending) {
    return CheckInStatusInView.PENDING;
  }

  if (checkInStatuses[0] === PassengerLegStatus.Success) {
    return CheckInStatusInView.SUCCESS;
  }

  return CheckInStatusInView.ACTION_REQUIRED;
};

export const badgeInfoPerStatus: Record<
  CheckInStatusInViewType,
  { variant: SignalVariantTypes; text: string }
> = {
  [CheckInStatusInView.SUCCESS]: {
    variant: signalVariants.POSITIVE,
    text: 'CheckIn.Success.Badge.Text',
  },
  [CheckInStatusInView.PENDING]: {
    variant: signalVariants.INFORMATION,
    text: 'CheckIn.Pending.Badge.Text',
  },
  [CheckInStatusInView.ACTION_REQUIRED]: {
    variant: signalVariants.CRITICAL,
    text: 'CheckIn.Failure.Badge.Text',
  },
};

export const getAirlineReferenceEmail = (
  flightInformation: FlightInformationType,
  customerEmail: string,
  pnr?: string | null,
): { email: string; isAlias: boolean } => {
  if (!pnr) {
    return { email: customerEmail, isAlias: false };
  }

  const pnrFlightInfo = flightInformation.find((flightInfo) => flightInfo.pnr === pnr);
  const shouldUseAliasEmail =
    !pnrFlightInfo?.externalFlightVerificationInfo.isExternalFlightVerified &&
    Boolean(pnrFlightInfo?.loginEmail);
  return {
    email: shouldUseAliasEmail ? pnrFlightInfo!!.loginEmail!! : customerEmail,
    isAlias: shouldUseAliasEmail,
  };
};
